// Bailey's remix: "make it bolder", "pink", "festive", "shorter words".
// Reads what the salon asked for, then picks four finished versions of their
// post from every design, colourway and tone of words, each different from
// the last, and says in a line what changed.
//
// Rule-based today (it's instant and never invents facts); the same inputs
// and outputs suit a Bailey endpoint later (docs/SOCIAL_BACKEND.md).
import { normHex, luminance } from '../../email-composer/email/theme'
import { FAMILIES, FAMILY_ORDER } from '../render/families'
import { colourwaysFor, fromColour } from '../render/colourways'
import { TEXT_SIZES } from '../render/render'
import { POST_TYPES, restyle, hasStyles, layoutsOf, layoutOf, photosFor } from './postTypes'
import { linesFor } from './phrases'

// ─── Colour words and moods ───
export const COLOUR_WORDS = {
  pink: '#F2A7BE', blush: '#F2C4CE', rose: '#E8A0B0', red: '#C8302A', coral: '#F07C68', orange: '#F28C38',
  peach: '#F4B89A', yellow: '#F2C94C', gold: '#C9A227', mustard: '#D4A017', lemon: '#F4E27A', green: '#3E7B4F',
  sage: '#9DB39A', mint: '#9FE0C1', olive: '#6B7040', teal: '#1F8A8A', blue: '#2F6FD6', navy: '#1C2A44',
  sky: '#8EC3EA', purple: '#6B3FA0', lilac: '#C8B6E2', lavender: '#B9A7DD', plum: '#5B1F6A', brown: '#6B4A34',
  chocolate: '#4A2F22', beige: '#E8D9C0', cream: '#F6EFE3', nude: '#E3C4AE', black: '#141414', white: '#F7F7F5',
  grey: '#8C8C8C', gray: '#8C8C8C', silver: '#B8BCC4', burgundy: '#6E1A2E', wine: '#6E1A2E', terracotta: '#C0603E',
  rust: '#B5522E', emerald: '#1F6B4F', cobalt: '#2445D8', lime: '#C6E84B',
}
const SEASONS = {
  festive: ['#B3261E', '#1F5C3A', '#C9A227'], christmas: ['#B3261E', '#1F5C3A', '#C9A227'],
  autumn: ['#C0603E', '#8A5A2B', '#D4A017'], fall: ['#C0603E', '#8A5A2B', '#D4A017'],
  winter: ['#1C2A44', '#8EC3EA', '#B8BCC4'], spring: ['#9DB39A', '#F2C4CE', '#F4E27A'],
  summer: ['#F28C38', '#F4B89A', '#9FE0C1'], valentine: ['#C8302A', '#F2A7BE', '#6E1A2E'], valentines: ['#C8302A', '#F2A7BE', '#6E1A2E'],
  halloween: ['#F28C38', '#141414', '#5B1F6A'], easter: ['#F4E27A', '#C8B6E2', '#9FE0C1'], pride: ['#E5484D', '#F2C94C', '#2F6FD6'],
}

const SEASON_SAID = {
  christmas: 'Christmas colours', fall: 'autumn colours', valentine: 'Valentine’s colours', valentines: 'Valentine’s colours',
  halloween: 'Halloween colours', easter: 'Easter colours', pride: 'Pride colours',
}

// Every chip the Bailey tool offers
export const CHIPS = [
  { id: 'surprise', label: 'Surprise me' },
  { id: 'bolder', label: 'Bolder' },
  { id: 'calmer', label: 'Calmer' },
  { id: 'luxe', label: 'More luxe' },
  { id: 'brand', label: 'In my brand' },
  { id: 'photo', label: 'Match my photo' },
  { id: 'warmer', label: 'Warmer colours' },
  { id: 'cooler', label: 'Cooler colours' },
  { id: 'darker', label: 'Darker' },
  { id: 'lighter', label: 'Lighter' },
  { id: 'shorter', label: 'Shorter words' },
  { id: 'playful', label: 'More playful' },
]

const has = (text, words) => words.some(w => new RegExp(`\\b${w}`, 'i').test(text))

// What was asked for, as intents
export const understand = (input = '') => {
  const text = String(input || '').toLowerCase()
  const want = { families: [], hexes: [], named: {}, moods: [], tone: null, size: 0, brand: false, photo: false, logo: false, format: null, surprise: false, filter: null, known: false }
  const mark = () => { want.known = true }
  if (has(text, ['surprise', 'random', 'anything', 'different', 'fresh take', 'mix it', 'remix', 'shuffle', 'new design', 'another'])) { want.surprise = true; mark() }
  if (has(text, ['bold', 'loud', 'punch', 'stand out', 'eye.?catch', 'pop\\b', 'strong', 'impact', 'poster', 'vibrant', 'bright colou?r'])) { want.families.push('poster'); want.moods.push('vivid'); mark() }
  if (has(text, ['calm', 'soft', 'quiet', 'gentle', 'minimal', 'clean', 'simple', 'spa\\b', 'subtle', 'relax'])) { want.families.push('quiet'); want.moods.push('soft'); mark() }
  if (has(text, ['luxe', 'luxur', 'elegant', 'classy', 'posh', 'premium', 'high.?end', 'sophisticat', 'editorial', 'magazine', 'classic', 'timeless', 'serif'])) { want.families.push('editorial'); want.tone = want.tone || 'luxe'; mark() }
  if (has(text, ['brand', 'my colour', 'my color', 'our colour', 'our color'])) { want.brand = true; mark() }
  if (has(text, ['match', 'from (my|the|our) (photo|picture)', '(photo|picture) colou?r'])) { want.photo = true; mark() }
  if (has(text, ['warm'])) { want.moods.push('warm'); mark() }
  if (has(text, ['cool', 'cold', 'fresh colours', 'icy'])) { want.moods.push('cool'); mark() }
  if (has(text, ['dark', 'moody', 'night', 'black background', 'evening'])) { want.moods.push('dark'); mark() }
  if (has(text, ['light', 'bright', 'airy', 'pale', 'white background'])) { want.moods.push('light'); mark() }
  if (has(text, ['black and white', 'black & white', 'mono', 'grey scale', 'grayscale', 'b&w'])) { want.filter = 'mono'; want.moods.push('mono'); mark() }
  if (has(text, ['short', 'fewer words', 'less text', 'less words', 'concise', 'snappy', 'punchier', 'brief'])) { want.tone = 'short'; mark() }
  if (has(text, ['fun\\b', 'funny', 'funnier', 'playful', 'cheeky', 'funny', 'witty', 'jokey', 'lively', 'emoji'])) { want.tone = 'playful'; mark() }
  if (has(text, ['warmer words', 'friendl', 'welcoming'])) { want.tone = 'warm'; mark() }
  if (has(text, ['bigger text', 'bigger words', 'larger', 'bigger headline', 'huge'])) { want.size = 1; mark() }
  if (has(text, ['smaller text', 'smaller words', 'smaller headline', 'tiny', 'less big'])) { want.size = -1; mark() }
  if (has(text, ['logo'])) { want.logo = true; mark() }
  if (has(text, ['story', 'stories', 'reel', 'tiktok', 'vertical', 'tall'])) { want.format = 'story'; mark() }
  if (has(text, ['feed\\b', 'grid post', 'square', 'portrait post'])) { want.format = 'post'; mark() }
  Object.keys(COLOUR_WORDS).forEach((w) => { if (new RegExp(`\\b${w}s?\\b`, 'i').test(text)) { want.hexes.push(COLOUR_WORDS[w]); want.named[COLOUR_WORDS[w]] = w === 'gray' ? 'grey' : w; mark() } })
  Object.keys(SEASONS).forEach((w) => { if (new RegExp(`\\b${w}\\b${w === 'pride' ? '(?! ourselves| myself| itself| in\\b)' : ''}`, 'i').test(text)) { want.hexes.push(...SEASONS[w]); SEASONS[w].forEach((h) => { if (!want.named[h]) want.named[h] = SEASON_SAID[w] || `${w} colours` }); if (!want.tone) want.tone = 'playful'; mark() } })
  const hex = text.match(/#[0-9a-f]{6}\b/gi)
  if (hex) { hex.forEach(h => want.hexes.push(normHex(h))); mark() }
  want.families = Array.from(new Set(want.families))
  want.moods = Array.from(new Set(want.moods))
  want.hexes = Array.from(new Set(want.hexes.filter(Boolean)))
  return want
}

// Chip ids read as the words they stand for
const CHIP_WORDS = {
  surprise: 'surprise me', bolder: 'bold', calmer: 'calm', luxe: 'luxe', brand: 'my brand', photo: 'match my photo',
  warmer: 'warm', cooler: 'cool', darker: 'dark', lighter: 'light', shorter: 'shorter', playful: 'playful',
}

// ─── Colour maths for moods ───
const rgb = (hex) => { const h = (normHex(hex) || '#000000').slice(1); return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16) / 255) }
const hsl = (hex) => {
  const [r, g, b] = rgb(hex)
  const mx = Math.max(r, g, b); const mn = Math.min(r, g, b); const l = (mx + mn) / 2
  if (mx === mn) return { h: 0, s: 0, l }
  const d = mx - mn
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
  let h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4
  h *= 60
  return { h, s, l }
}
const hueGap = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d }
// The colour a design mostly shows: the ground (a poster's and a bold
// layout's is its accent; a dark layout's, its words colour)
const groundOf = (c) => (c.fam === 'poster' || c.ground === 'accent' ? c.colours[2] : c.ground === 'ink' ? c.colours[1] : c.colours[0])
const moodScore = (c, moods) => {
  if (!moods.length) return 0
  const { colours } = c
  const g = groundOf(c)
  const { h, s } = hsl(g)
  const lum = luminance(g)
  const all = colours.map(hsl)
  const avgSat = all.reduce((a, c) => a + c.s, 0) / all.length
  let score = 0
  moods.forEach((m) => {
    if (m === 'warm') score += (s > 0.08 && (h < 60 || h > 320)) ? 2 : (h < 90 && s > 0.05 ? 1 : -1)
    if (m === 'cool') score += (s > 0.06 && h > 150 && h < 290) ? 2 : -1
    if (m === 'dark') score += lum < 0.15 ? 2 : lum < 0.35 ? 1 : -2
    if (m === 'light') score += lum > 0.7 ? 2 : lum > 0.45 ? 0.5 : -2
    if (m === 'vivid') score += avgSat > 0.45 ? 2 : avgSat > 0.3 ? 1 : -1
    if (m === 'soft') score += avgSat < 0.35 && lum > 0.5 ? 2 : -0.5
    if (m === 'mono') score += avgSat < 0.12 ? 2 : -1
  })
  return score
}
const colourScore = (c, hexes) => {
  if (!hexes.length) return 0
  const g = hsl(groundOf(c))
  return Math.max(...hexes.map((x) => { const t = hsl(x); return t.s < 0.12 ? (Math.abs(t.l - g.l) < 0.2 && g.s < 0.2 ? 3 : 0) : (g.s > 0.1 && hueGap(t.h, g.h) < 22 ? 3 : hueGap(t.h, g.h) < 45 ? 1 : 0) }))
}

// A little seeded randomness, so "surprise me" differs each time but tests don't
const rng = (seed) => { let a = (seed >>> 0) || 1; return () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296 } }

const plain = (s) => String(s || '').replace(/\*/g, '')
const lower = (s) => String(s || '').charAt(0).toLowerCase() + String(s || '').slice(1)

// ─── The remix ───
// Returns { said, variants: [{ post, note }] }: four versions (fewer only
// when there aren't four different ones to make). `keep` lists words the
// salon has just given, which the versions leave as they are.
export const remix = (post, { prompt = '', chip = null, kit = {}, trade = post.trade || 'hair', seed = 1, count = 4, keep = [] } = {}) => {
  const asked = chip ? CHIP_WORDS[chip] || chip : prompt
  const want = understand(asked)
  const random = rng(seed * 7919 + asked.length)
  const opts = { brandColour: kit.accent, photoColour: post.photoColour }
  const styles = hasStyles(post.type) ? (want.families.length ? want.families : FAMILY_ORDER) : []
  // The job's own layouts join in; asking for bold, calm or classic keeps to
  // the themes with that kind of headline
  const TYPE_OF = { poster: 'heavy', quiet: 'sans', editorial: 'serif' }
  const types = want.families.map(f => TYPE_OF[f]).filter(Boolean)
  const layouts = layoutsOf(post.type)
  const custom = !!(post.tune && post.tune.colours)
  const designOf = (c) => (c.layout ? `layouts:${c.layout}` : c.fam)

  // Candidates: every style x colourway and layout x theme allowed, plus
  // colours made from the colours asked for
  let pool = []
  const add = (fam, layout, ground) => {
    colourwaysFor(fam, opts).forEach((cw) => {
      if (want.brand && cw.id !== 'brand') return
      if (want.photo && cw.id !== 'photo') return
      if (layout && types.length && cw.type && !types.includes(cw.type)) return
      pool.push({ fam, layout, ground, colourway: cw.id, name: cw.name, colours: cw.colours })
    })
    want.hexes.forEach((hex, i) => pool.push({ fam, layout, ground, colourway: (colourwaysFor(fam, {})[0] || {}).id, name: want.named[hex] || 'your colour', colours: fromColour(fam, hex), made: true, i }))
  }
  styles.forEach(fam => add(fam, null, null))
  layouts.forEach(l => add('layouts', l, layoutOf(post.type, l).ground || null))
  // A design never offers itself back
  const isNow = (c) => designOf(c) === (post.family === 'layouts' ? `layouts:${post.layout}` : post.family) && c.colourway === post.colourway
  pool = pool.filter(c => c.made || custom || !isNow(c))
  if (!pool.length) {
    // Nothing matched ("in my brand" with no brand colour): fall back to everything
    ;(hasStyles(post.type) ? FAMILY_ORDER : []).forEach(fam => colourwaysFor(fam, opts).forEach((cw) => { pool.push({ fam, colourway: cw.id, name: cw.name, colours: cw.colours }) }))
    layouts.forEach(l => colourwaysFor('layouts', opts).forEach((cw) => { pool.push({ fam: 'layouts', layout: l, ground: layoutOf(post.type, l).ground || null, colourway: cw.id, name: cw.name, colours: cw.colours }) }))
    pool = pool.filter(c => !isNow(c))
  }
  const designs = Array.from(new Set(pool.map(designOf)))
  const scored = pool.map(c => ({
    ...c,
    score: moodScore(c, want.moods) + colourScore(c, want.hexes) * 1.5 + (c.made ? 2.5 : 0)
      + (want.families.includes(c.fam) ? 1 : 0) + (want.surprise || !want.known ? random() * 3 : random() * 0.6),
  })).sort((a, b) => b.score - a.score)

  // Four different: prefer a new design or colour each time
  const picks = []
  const same = (a, b) => designOf(a) === designOf(b) && a.colourway === b.colourway && !a.made && !b.made
  scored.forEach((c) => {
    if (picks.length >= count) return
    const sameDesign = picks.filter(p => designOf(p) === designOf(c)).length
    if (picks.some(p => same(p, c))) return
    if (designs.length > 1 && sameDesign >= Math.ceil(count / Math.min(designs.length, count)) && scored.some(o => !picks.includes(o) && picks.every(p => designOf(p) !== designOf(o)))) return
    picks.push(c)
  })
  scored.forEach((c) => { if (picks.length < count && !picks.includes(c) && !picks.some(p => same(p, c))) picks.push(c) })

  // Words in the tone asked for (a different line for each version)
  const def = POST_TYPES[post.type]
  const wordFields = def.fields.filter(f => !f.locked && (f.type === 'text' || f.type === 'textarea')).map(f => f.key)
  const words = (i) => {
    if (!want.tone) return null
    const out = {}
    ;['headline', 'title', 'eyebrow', 'line', 'label'].forEach((k) => {
      if (!wordFields.includes(k) || keep.includes(k)) return
      const lines = linesFor(post.type, k, want.tone, trade, post.content[k])
      if (lines.length) out[k] = lines[i % lines.length]
    })
    if (wordFields.includes('cta') && !keep.includes('cta')) {
      const ctas = linesFor(post.type, 'cta', want.tone, trade, post.content.cta)
      if (ctas.length) out.cta = ctas[0]
    }
    if (want.tone === 'short' && wordFields.includes('detail') && !keep.includes('detail') && String(post.content.detail || '').length > 26) out.detail = ''
    return Object.keys(out).length ? out : null
  }

  const sizeNow = TEXT_SIZES.indexOf((post.tune && post.tune.size) || 1)
  const variants = picks.map((c, i) => {
    let next = restyle(post, c.fam, c.colourway, c.layout)
    const tune = { ...(post.tune || {}) }
    delete tune.colours
    if (c.fam !== post.family) { delete tune.font; delete tune.textFont }
    if (c.made) tune.colours = { paper: c.colours[0], ink: c.colours[1], accent: c.colours[2], pop: c.colours[3], ...(c.colours[4] ? { on: c.colours[4] } : {}) }
    const notes = [`${c.layout ? layoutOf(post.type, c.layout).name : FAMILIES[c.fam].name}, ${c.made ? c.name : lower(c.name)}`]
    if (want.size) {
      const idx = Math.max(0, Math.min(TEXT_SIZES.length - 1, (sizeNow < 0 ? 2 : sizeNow) + want.size))
      tune.size = TEXT_SIZES[idx] === 1 ? undefined : TEXT_SIZES[idx]
      notes.push(want.size > 0 ? 'bigger headline' : 'smaller headline')
    } else if (c.fam === 'poster' && want.moods.includes('vivid') && sizeNow <= 2) {
      tune.size = TEXT_SIZES[3]
      notes.push('bigger headline')
    }
    if (want.logo && kit.logo) { tune.brand = 'logo'; notes.push('your logo') }
    Object.keys(tune).forEach(k => tune[k] === undefined && delete tune[k])
    next = { ...next, tune }
    const w = words(i)
    if (w) {
      next = { ...next, content: { ...next.content, ...w }, touched: { ...(next.touched || {}), ...Object.keys(w).reduce((a, k) => ({ ...a, [k]: true }), {}) } }
      const head = w.headline || w.title || w.eyebrow
      notes.push(head ? `“${plain(head)}”` : `${want.tone} words`)
    }
    // Moods reach the photos too
    const filter = want.filter || (want.moods.includes('warm') ? 'warm' : want.moods.includes('cool') ? 'cool' : want.moods.includes('light') ? 'bright' : null)
    if (filter && photosFor(next).length) {
      const photos = { ...next.photos }
      Object.keys(photos).forEach((k) => { if (photos[k] && photos[k].src) photos[k] = { ...photos[k], filter } })
      next = { ...next, photos }
      notes.push(`${filter === 'mono' ? 'black and white' : filter} photo`)
    }
    if (want.format) next = { ...next, format: want.format }
    next = { ...next, caption: post.caption && post.caption.edited ? post.caption : null }
    return { post: next, note: notes.join(', ') }
  })

  const said = !want.known
    ? 'I wasn’t sure what you meant, so here are four fresh takes. Try words like bolder, calmer, pink, festive or shorter.'
    : want.brand && !normHex(kit.accent)
      ? 'You haven’t set a brand colour yet, so here are four other takes. Set yours in Marketing, Config.'
      : want.photo && !normHex(post.photoColour)
        ? 'Add a photo first and I’ll match the colours to it. Until then, here are four other takes.'
        : want.brand
          ? 'Here’s your post in your brand colours, in each style. Tap one to use it.'
          : want.photo
            ? 'Here are colours taken from your photo, in each style. Tap one to use it.'
            : 'Here are four takes. Tap one to use it, and undo brings yours back.'
  return { said, want, variants }
}
